import { lazy, useEffect, useMemo } from "react";
import { selectCurrentStage, useJourneyStore } from "../game/state/journeyMachine";
import { JOURNEY_STAGES } from "../data/journeyStages";
import { StageRenderer } from "../game/engine/Renderer";
import { AssetManifestLoader } from "../game/engine/AssetManifestLoader";
import { SedeScene } from "../game/scenes/sede/SedeScene";
import { StagePanel } from "../accessibility/StagePanel";
import { SidePanel } from "../components/SidePanel";

/** Segundos que el modo guiado permanece en cada etapa antes de avanzar solo. */
const GUIDED_DWELL_SECONDS = 14;

// Carga diferida: el modo "Red nacional CALE" solo se descarga y monta
// cuando mode === "red-nacional". StageRenderer ya envuelve en <Suspense>.
const NationalNetworkScene = lazy(() =>
  import("../game/scenes/national/NationalNetwork").then((m) => ({
    default: m.NationalNetworkScene,
  })),
);

export function JourneyScreen() {
  const stage = useJourneyStore(selectCurrentStage);
  const stageIndex = useJourneyStore((s) => s.stageIndex);
  const mode = useJourneyStore((s) => s.mode);
  const viewMode = useJourneyStore((s) => s.viewMode);
  const guided = useJourneyStore((s) => s.guided);
  const paused = useJourneyStore((s) => s.paused);
  const nextStage = useJourneyStore((s) => s.nextStage);

  // Modo guiado: avanza solo por las 13 etapas cada GUIDED_DWELL_SECONDS.
  // Se detiene en la última; Pausa lo congela; Siguiente/Anterior reinician
  // la cuenta de la etapa actual.
  useEffect(() => {
    if (!guided || paused || mode === "red-nacional") return;
    if (stageIndex >= JOURNEY_STAGES.length - 1) return;
    const timer = window.setTimeout(nextStage, GUIDED_DWELL_SECONDS * 1000);
    return () => window.clearTimeout(timer);
  }, [guided, paused, stageIndex, mode, nextStage]);

  // Escena única de la sede: se monta una vez y las etapas se recorren
  // con waypoints de cámara; la red nacional es una escena aparte con
  // carga diferida (React.lazy) que solo se monta en mode "red-nacional".
  const scene = useMemo(() => (mode === "red-nacional" ? <NationalNetworkScene /> : <SedeScene />), [mode]);

  return (
    <main className="flex flex-1 flex-col md:flex-row">
      <section
        aria-label="Escena de la etapa"
        className="relative min-h-[50vh] flex-1 md:sticky md:top-24 md:h-[calc(100vh-6rem)] md:min-h-0"
      >
        {viewMode === "3d" || mode === "red-nacional" ? (
          <AssetManifestLoader>
            <StageRenderer>{scene}</StageRenderer>
          </AssetManifestLoader>
        ) : (
          <div className="h-full bg-[var(--cale-surface)] p-4">
            <StagePanel stage={stage} index={stageIndex} total={JOURNEY_STAGES.length} />
          </div>
        )}
      </section>
      <SidePanel />
    </main>
  );
}
